import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  Button,
  getPaymentRequired,
  Loader,
  Spacer,
  TranslationProvider,
  Divider,
} from '@resy/ui-kitchen';

import getOrderFromReservationDetails from '../SummaryPage/getOrderFromReservationDetails';
import OrderSummary from '../OrderSummary';
import PaymentMethodDropdown from '../PaymentMethodDropdown';
import reservationDetailsShape, { options as serviceChargeOptionsShape } from '../SummaryPage/reservationDetailsShape';
import SummarySection, {
  SummarySectionBody,
  SummarySectionHeader,
} from '../SummarySection';
import WidgetNav from '../WidgetNav';
import WidgetTitle from '../WidgetTitle';
import WidgetPage, {
  WidgetPageBody,
  WidgetPageFooter,
  WidgetPageHeader,
} from '../WidgetPage';

import Lock from '../../../images/lock.svg';
import Check from '../../../images/ic_checkmark-success_20x20.svg';

import './SelectCreditCardPage.scss';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { getURLSearchParams, setSearchParams } from '../../../../../ui-kitchen/src/utils/searchParamUtils';
import { getTranslation } from '../../../../../ui-kitchen/src/providers/TranslationProvider';

const selectCreditCardPagePropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  backPath: PropTypes.string,
  BookingState: PropTypes.shape({
    nonce: PropTypes.string,
  }).isRequired,
  bookOnBehalfOf: PropTypes.shape({
    error: PropTypes.string,
    selected: PropTypes.bool,
  }),
  bookingState: PropTypes.shape({
    nonce: PropTypes.string,
  }).isRequired,
  canBook: PropTypes.bool,
  canBookOnBehalf: PropTypes.bool,
  clearError: PropTypes.func,
  errors: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        data: PropTypes.shape({}),
        ctaTextKey: PropTypes.string,
        message: PropTypes.string,
        onClick: PropTypes.func,
        statusCode: PropTypes.string,
      }),
    ])
  ),
  isAuthenticated: PropTypes.bool.isRequired,
  legalText: PropTypes.string,
  navigate: PropTypes.func.isRequired,
  navSearch: PropTypes.shape({}).isRequired,
  onBookingInfoChange: PropTypes.func,
  onBookingTypeChange: PropTypes.func,
  paymentId: PropTypes.number,
  paymentMethods: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
    })
  ),
  pending: PropTypes.bool,
  preferredLanguage: PropTypes.string,
  reservationDetails: reservationDetailsShape,
  reservationInfo: PropTypes.shape({
    isGda: PropTypes.bool,
    colors: PropTypes.shape({
      background: PropTypes.string,
      font: PropTypes.string,
    }),
    event: PropTypes.shape({
      title: PropTypes.string,
    }),
    partySize: PropTypes.number,
  }),
  serviceChargeChange: PropTypes.func,
  serviceChargeOptions: serviceChargeOptionsShape,
  specs: PropTypes.shape({
    day: PropTypes.string,
    time: PropTypes.string,
  }),
  templateAbout: PropTypes.shape({
    body: PropTypes.string,
  }),
  thunks: PropTypes.shape({
    attemptBook: PropTypes.func,
    setPaymentId: PropTypes.func,
  }).isRequired,
  toggleVenueCommunicationOptIn: PropTypes.func.isRequired,
  venueCommunicationOptIn: PropTypes.bool,
  venueInfo: PropTypes.shape({
    name: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    partnershipBranding: PropTypes.shape({
      name: PropTypes.string,
      logo: PropTypes.string,
    }),
  }),
  viewPortHeight: PropTypes.number,
};

class SelectCreditCardPage extends React.Component {
  state = {
    /* eslint-disable-next-line react/no-unused-state */
    items: undefined,
    // for analytics
    titleOpacity: 0,
  }

  static propTypes = selectCreditCardPagePropTypes;

  static defaultProps = {
    backPath: undefined,
    bookOnBehalfOf: undefined,
    canBook: true,
    canBookOnBehalf: false,
    clearError: undefined,
    errors: undefined,
    legalText: '',
    onBookingInfoChange: () => {},
    onBookingTypeChange: () => {},
    paymentId: undefined,
    paymentMethods: undefined,
    pending: false,
    preferredLanguage: 'en-US',
    reservationDetails: undefined,
    reservationInfo: undefined,
    serviceChargeChange: () => {},
    serviceChargeOptions: undefined,
    specs: undefined,
    templateAbout: undefined,
    venueCommunicationOptIn: false,
    venueInfo: undefined,
    viewPortHeight: undefined,
  }

  getButtonStyles = () => {
    const {
      reservationInfo,
    } = this.props;

    const colors = reservationInfo?.colors;

    if (!colors) {
      return null;
    }

    return { colors };
  }

  onAddNewPaymentMethod = () => {
    const { navigate } = this.props;

    navigate('/add-payment');
    setSearchParams('next', '/reservation-details');
  }

  isLoading = () => {
    const { pending, reservationDetails } = this.props;

    return pending || !reservationDetails;
  }

  onBook = () => {
    const {
      isAuthenticated,
      paymentMethods,
      thunks: {
        attemptBook,
      } = {},
    } = this.props;

    attemptBook({
      isAuthenticated,
      cardsOnFile: !!paymentMethods?.length,
    });
  }

  onPaymentMethodSelect = (response) => {
    const {
      thunks: {
        setPaymentId,
      },
    } = this.props;
    const {
      currentTarget: {
        value,
      },
    } = response;

    setPaymentId(+value);
  }

  onServiceChargeChange = (val) => {
    const {
      thunks: {
        commitDetailsThunk,
        setServiceCharge,
      } = {},
    } = this.props;
    const { value } = val;

    // value is going to be a string
    // eg '0%' || '15%' || '25%'
    if (value) {
      setServiceCharge(value);
      // update the reservation on the server
      commitDetailsThunk();
    }
  }

  renderAddCard = () => {
    const {
      BookingState,
    } = this.props;

    return (
      <TranslationProvider translationKey={BookingState?.nonce ? 'credit_card_change_label' : 'credit_card_add_label'}>
        <Button
          className="SelectCreditCardPage__add-card"
          data-test-id="order_summary_page-button-add_card"
          onClick={this.onAddNewPaymentMethod}
          variant="link"
        >
          {BookingState?.nonce ? 'Change card' : 'Add new card'}
        </Button>
      </TranslationProvider>
    );
  }

  renderBook = () => {
    const {
      canBook,
      reservationInfo,
    } = this.props;

    const isGda = reservationInfo?.isGda;

    const key = 'complete_reservation';
    const str = 'Complete Reservation';

    const colors = (!isGda && this.getButtonStyles()) || null;

    return (
      <div className="SummaryPage__book">
        <Button
          data-test-id="select_credit_card_page-button-book"
          disabled={!canBook || this.isLoading()}
          lg
          onClick={this.onBook}
          {...colors}
          variant={isGda ? 'rga' : 'primary'}
        >
          <TranslationProvider translationKey={key}>
            <span>{str}</span>
          </TranslationProvider>
        </Button>
      </div>
    );
  }

  renderLoader = () => {
    if (!this.isLoading()) {
      return null;
    }
    return <Loader />;
  }

  renderOrderSummary = () => {
    const {
      canBook,
      preferredLanguage,
      reservationDetails,
      serviceChargeOptions,
      specs: {
        tableType,
      } = {},
      venueInfo: {
        locale: {
          currency: currencyCode,
        } = {},
        isUSLocation,
        taxIncluded,
      } = {},
    } = this.props;

    if (!canBook) {
      return null;
    }

    if (!reservationDetails) {
      return null;
    }

    const order = {
      ...getOrderFromReservationDetails(reservationDetails, serviceChargeOptions),
      currencyCode,
      isUSLocation,
      locale: preferredLanguage,
      reservationItemName: tableType,
      serviceChargeChange: this.onServiceChargeChange,
      taxIncluded,
    };

    if (!order.total) {
      return null;
    }

    return (
      <>
        <SummarySection className="SelectCreditCardPage__order-summary">
          <SummarySectionHeader>
            <TranslationProvider translationKey="payment_details_header">
              <span>Payment Details</span>
            </TranslationProvider>
          </SummarySectionHeader>
          <SummarySectionBody>
            <OrderSummary {...order} />
          </SummarySectionBody>
        </SummarySection>
        <Spacer height="30" />
      </>
    );
  }

  renderCancellationPolicy = () => {
    const {
      reservationDetails,
    } = this.props;

    const policy = reservationDetails?.cancellation?.display?.policy;

    if (!policy?.length) return null;

    return (
      <>
        <SummarySection className="SelectCreditCardPage__policy">
          <SummarySectionHeader>
            <TranslationProvider translationKey="cancellation_policy_header">
              <span>Cancellation Policy</span>
            </TranslationProvider>
          </SummarySectionHeader>
          <SummarySectionBody className="SelectCreditCardPage__cancellation">
            <ul>
              {policy.map((p) => <li key={p.substring(0, 9)}>{p}</li>)}
            </ul>
          </SummarySectionBody>
        </SummarySection>
        <Spacer height="30" />
      </>
    );
  }

  renderPaymentMethod = () => {
    const {
      paymentMethods,
      BookingState,
      isAuthenticated,
      paymentId,
      reservationDetails,
      bookOnBehalfOf: {
        selected: bookOnBehalfOf,
      } = {},
    } = this.props;

    if (!getPaymentRequired(reservationDetails) || !isAuthenticated) {
      return null;
    }

    let content;

    if (bookOnBehalfOf && !!BookingState?.nonce) {
      content = (
        <>
          <div
            className="SelectCreditCardPage__payment-obscured"
          >
            &#8226;&#8226;&#8226;&#8226;
          </div>
        </>
      );
    } else {
      content = !paymentMethods?.length ? null : (
        <PaymentMethodDropdown
          addNew={this.onAddNewPaymentMethod}
          bookOnBehalfOf={false}
          disabled={this.isLoading()}
          onPaymentMethodSelect={this.onPaymentMethodSelect}
          options={paymentMethods}
          selectedPaymentMethod={paymentId}
        />
      );
    }

    return (
      <>
        <SummarySection>
          <SummarySectionHeader>
            <TranslationProvider translationKey="payment_method_header">
              <span>
                Payment Method
              </span>
            </TranslationProvider>
            <Lock className="SelectCreditCardPage__lock" width={20} height={20} />
          </SummarySectionHeader>
          <SummarySectionBody className="SelectCreditCardPage__payment">
            {content}
            {this.renderAddCard()}
          </SummarySectionBody>
        </SummarySection>
        <Divider marginVertical={20} />
        <Spacer height="10" />
      </>
    );
  }

  renderWidgetNav = () => {
    const {
      Analytics,
      backPath,
      navigate,
      navSearch,
      venueInfo: {
        name,
      } = {},
    } = this.props;

    const { titleOpacity } = this.state;

    return (
      <WidgetNav
        Analytics={Analytics}
        backPath={backPath}
        navigate={navigate}
        showBack
        showProfileMenu
        title={name}
        search={navSearch}
        titleOpacity={titleOpacity}
      />
    );
  }

  renderWidgetTitle = () => {
    const {
      reservationInfo,
    } = this.props;

    if (!reservationInfo) {
      return null;
    }
    const searchParams = getURLSearchParams();
    const isCreditCardAdded = searchParams.get('added') === 'true';
    return (
      <>
        {isCreditCardAdded
          ? (
            <WidgetTitle horizontalRule={false}>
              <TranslationProvider translationKey="credit_card_added_header">
                <span>
                  Credit Card Added
                </span>
              </TranslationProvider>
              <span className="SelectCreditCardPage__check">
                <Check width={26} height={26} />
              </span>
            </WidgetTitle>
          )
          : (
            <WidgetTitle horizontalRule={false}>
              <TranslationProvider translationKey="select_credit_card_header">
                <span>Select Credit Card</span>
              </TranslationProvider>
            </WidgetTitle>
          )}
        <TranslationProvider translationKey="select_credit_card_desc">
          <p>
            This venue requires a credit card to book because this reservation is either pre-paid, has
            pre-paid items, or may have cancellation fee.
          </p>
        </TranslationProvider>
      </>
    );
  }

  render() {
    const searchParams = getURLSearchParams();
    const isCreditCardAdded = searchParams.get('added') === 'true';
    const ariaLabelText = isCreditCardAdded ? getTranslation({ translationKey: 'credit_card_added_header', fallback: 'Credit Card Added' })
      : getTranslation({ translationKey: 'select_credit_card_header', fallback: 'Select Credit Card' });
    return (
      <WidgetPage className="SelectCreditCardPage" ariaLabel={ariaLabelText}>
        {this.renderWidgetNav()}
        <WidgetPageHeader>
          {this.renderWidgetTitle()}
        </WidgetPageHeader>
        <WidgetPageBody>
          {this.renderLoader()}
          {this.renderPaymentMethod()}
          {this.renderOrderSummary()}
          {this.renderCancellationPolicy()}
          <Spacer height="30" />
        </WidgetPageBody>
        <WidgetPageFooter>
          {this.renderBook()}
        </WidgetPageFooter>
      </WidgetPage>
    );
  }
}

const mapStateToProps = (state) => {
  const { resyRouter } = state;
  return {
    navSearch: resyRouter.search,
    isAuthenticated: selectIsAuthenticated(state),
  };
};

export { selectCreditCardPagePropTypes, SelectCreditCardPage as ComponentUnderTest };
export default connect(mapStateToProps)(SelectCreditCardPage);
